/**
 * GoEye lens+frame bundle linkage.
 *
 * A prescription purchase is TWO cart lines — the lens (vendor LensAdvizor) and the frame — that
 * only make sense together. Both lines are added in one /cart/add.js call carrying a shared
 * `_bundle_id` (and `_bundle_role`), so the pair can always be found again from cart data.
 *
 * This file is the single authority on that linkage. It guarantees:
 *
 *   1. Removing EITHER side removes the whole bundle, in ONE atomic /cart/update.js — never a
 *      half-state where the frame is gone but the lens is still billed.
 *   2. Changing the quantity of either side moves the whole bundle to that quantity.
 *   3. A bundle that got broken by something this file did not drive — the /cart page, another
 *      app, a stale tab, an out-of-stock drop at checkout — is healed on the next cart mutation
 *      and on page load. One side missing means the bundle is void, so the survivors go too.
 *
 * It works off /cart.js, never off the rendered DOM, so a partner line that is hidden (the
 * Fitting Fee row), filtered out, or simply not painted yet is still included.
 *
 * Public API (window.GoeyeBundle):
 *   keysFor(key)            -> Promise<string[]>  every line key in this line's bundle
 *   removeBundleForKey(key) -> Promise<cart|null> remove the whole bundle; null when not bundled
 *   setBundleQuantity(k, q) -> Promise<cart|null> set the whole bundle to q; null when not bundled
 *   reconcile()             -> Promise<bool>      heal broken bundles; true when it changed the cart
 */
(function () {
  'use strict';

  if (window.GoeyeBundle) return;

  var GID = '_bundle_id';
  // Bundles stamped by the previous build used this name. Read it so carts already in flight
  // (and any order placed before this ships) still cascade.
  var GID_LEGACY = '_bundle_group_id';
  var ROLE = '_bundle_role';

  // ---------------------------------------------------------------- line classification

  function props(item) {
    return (item && item.properties) || {};
  }

  function gidOf(item) {
    var p = props(item);
    var g = p[GID] || p[GID_LEGACY];
    return g ? String(g) : '';
  }

  /**
   * The shared Fitting Fee line. It is ONE line for the whole cart (its quantity tracks the total
   * lens count — see the auto fitting fee system in cart-drawer.liquid), so it belongs to no single
   * bundle and is never removed as part of one.
   */
  function isFeeLine(item) {
    if (!item) return false;
    var p = props(item);
    if (p._fitting_fee === 'true') return true;
    var sku = String(item.sku || '').toUpperCase();
    if (sku === 'FITTING FEE' || sku === 'FITTING-FEE') return true;
    var handle = String(item.handle || item.product_handle || '').toLowerCase();
    if (handle === 'fitting-fee') return true;
    var title = String(item.product_title || item.title || '').toUpperCase();
    return title.indexOf('FITTING FEE') !== -1;
  }

  /**
   * Is this the LENS half of a bundle? `_bundle_role` is authoritative for lines added since this
   * file shipped; the heuristics below keep bundles created by an older build of the theme (already
   * sitting in shoppers' carts) working.
   */
  function isLensLine(item) {
    var p = props(item);
    if (p[ROLE] === 'lens') return true;
    if (p[ROLE] === 'frame') return false;
    if (item.product_type === 'Lens' || item.product_type === 'Service') return true;
    var vendor = String(item.vendor || '').toLowerCase().trim();
    if (vendor === 'lensadvizor' || vendor === 'lensadvizor-service') return true;
    return !!(p['Power Type'] || p['Lens Brand'] || p['Associated Frame'] || p['Lens Type']);
  }

  /**
   * Group the cart's bundled lines by bundle id. The shared Fitting Fee line is left out — it is
   * not a bundle member even on the rare legacy line that carries a bundle id.
   */
  function groupsOf(items) {
    var groups = {};
    (items || []).forEach(function (item) {
      var gid = gidOf(item);
      if (!gid || isFeeLine(item)) return;
      if (!groups[gid]) groups[gid] = { gid: gid, lens: [], frame: [], all: [] };
      var side = isLensLine(item) ? 'lens' : 'frame';
      groups[gid][side].push(item);
      groups[gid].all.push(item);
    });
    return groups;
  }

  /** A bundle is intact only while BOTH halves are present. Either side missing voids it. */
  function isBroken(group) {
    return group.lens.length === 0 || group.frame.length === 0;
  }

  // ---------------------------------------------------------------- cart plumbing

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('cart.js ' + r.status);
        return r.json();
      });
  }

  /**
   * ONE atomic /cart/update.js for every key in `updates`. Sending the keys as separate sequential
   * /cart/change.js requests leaves the cart readable in a partial state between them (frame gone,
   * lens still counted) and strands it there if the second request fails.
   */
  function applyUpdates(updates) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (r) {
      if (!r.ok) throw new Error('cart/update.js ' + r.status);
      return r.json();
    });
  }

  /** Repaint whatever cart UI is on the page after we have changed the cart behind its back. */
  function announce(cart) {
    try {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    } catch (e) { /* older browsers: the refresh below still repaints */ }
    try {
      if (window.goeyeCartDrawer && window.goeyeCartDrawer.refresh) {
        window.goeyeCartDrawer.refresh(true);
      }
    } catch (e) { /* drawer absent on this page */ }
  }

  // `busy` keeps our own /cart/update.js from re-entering through the mutation hook below.
  var busy = false;

  /** True while some OTHER cart script is mid-rewrite, so a transient cart is not mistaken for broken. */
  function otherScriptBusy() {
    return !!window.__goeyeUpsellReconciling;
  }

  // ---------------------------------------------------------------- public operations

  /** Every line key in `key`'s bundle, `key` included. Empty when the line is not bundled. */
  function keysFor(key) {
    if (!key) return Promise.resolve([]);
    return getCart().then(function (cart) {
      var items = (cart && cart.items) || [];
      var target = null;
      for (var i = 0; i < items.length; i++) {
        if (items[i].key === key) { target = items[i]; break; }
      }
      if (!target) return [];
      var gid = gidOf(target);
      if (!gid || isFeeLine(target)) return [];
      var group = groupsOf(items)[gid];
      if (!group) return [];
      return group.all.map(function (it) { return it.key; });
    }).catch(function () { return []; });
  }

  /**
   * Remove the whole bundle `key` belongs to. Resolves with the new cart, or with null when the
   * line is not part of a bundle — the caller then removes that single line the ordinary way.
   */
  function removeBundleForKey(key) {
    return keysFor(key).then(function (keys) {
      if (keys.length === 0) return null;
      var updates = {};
      keys.forEach(function (k) { updates[k] = 0; });
      busy = true;
      return applyUpdates(updates)
        .then(function (cart) { busy = false; announce(cart); return cart; })
        .catch(function (err) { busy = false; throw err; });
    });
  }

  /**
   * Move every line in `key`'s bundle to `quantity`. A lens is only ever bought one-per-frame, so
   * the two halves must stay at the same count. Resolves null when the line is not bundled.
   */
  function setBundleQuantity(key, quantity) {
    var qty = parseInt(quantity, 10);
    if (!Number.isFinite(qty) || qty < 0) return Promise.resolve(null);
    if (qty === 0) return removeBundleForKey(key);
    return keysFor(key).then(function (keys) {
      if (keys.length === 0) return null;
      var updates = {};
      keys.forEach(function (k) { updates[k] = qty; });
      busy = true;
      return applyUpdates(updates)
        .then(function (cart) { busy = false; announce(cart); return cart; })
        .catch(function (err) { busy = false; throw err; });
    });
  }

  /**
   * Heal the cart: every bundle missing one of its halves loses its survivors too.
   *
   * The broken set is confirmed against a SECOND read a moment later before anything is removed.
   * A bundle can be legitimately half-written for an instant — the ₹199 → ₹799 upsell swap adds the
   * replacement frame under the same bundle id and drops the old one — and a frame deleted out from
   * under its lens on the strength of one read would be real data loss.
   */
  function reconcile() {
    if (busy || otherScriptBusy()) return Promise.resolve(false);

    return getCart().then(function (first) {
      var brokenKeys = brokenKeysOf(first);
      if (brokenKeys.length === 0) return false;

      return wait(350).then(getCart).then(function (second) {
        if (busy || otherScriptBusy()) return false;
        var stillBroken = brokenKeysOf(second);
        // Only the keys broken in BOTH reads — anything that healed itself in between was transient.
        var confirmed = stillBroken.filter(function (k) { return brokenKeys.indexOf(k) !== -1; });
        if (confirmed.length === 0) return false;

        var updates = {};
        confirmed.forEach(function (k) { updates[k] = 0; });
        busy = true;
        return applyUpdates(updates)
          .then(function (cart) { busy = false; announce(cart); return true; })
          .catch(function (err) { busy = false; console.error('[GoeyeBundle] reconcile failed:', err); return false; });
      });
    }).catch(function (err) {
      console.error('[GoeyeBundle] reconcile failed:', err);
      return false;
    });
  }

  function brokenKeysOf(cart) {
    var groups = groupsOf((cart && cart.items) || []);
    var keys = [];
    Object.keys(groups).forEach(function (gid) {
      if (isBroken(groups[gid])) {
        groups[gid].all.forEach(function (it) { keys.push(it.key); });
      }
    });
    return keys;
  }

  function wait(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  // ---------------------------------------------------------------- mutation hook

  // Any cart write by any script on the page can break a bundle. Reconcile after each one, debounced
  // so a burst (add lens + frame, then the fitting-fee sync) settles into a single pass.
  var pending = null;
  function scheduleReconcile() {
    if (pending) clearTimeout(pending);
    pending = setTimeout(function () {
      pending = null;
      reconcile();
    }, 450);
  }

  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js|\.json)?([?#]|$)/;

  function hookFetch() {
    var original = window.fetch;
    if (typeof original !== 'function' || window.__goeyeBundleFetchHooked) return;
    window.__goeyeBundleFetchHooked = true;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isWrite = CART_WRITE.test(url);
      var p = arguments.length > 1 ? original.call(this, input, init) : original.call(this, input);
      if (!isWrite) return p;
      return p.then(function (resp) {
        if (!busy && resp && resp.ok) scheduleReconcile();
        return resp;
      });
    };
  }

  function hookXhr() {
    if (!window.XMLHttpRequest || window.__goeyeBundleXhrHooked) return;
    window.__goeyeBundleXhrHooked = true;
    var open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        if (CART_WRITE.test(String(url || ''))) {
          this.addEventListener('load', function () {
            if (!busy && this.status >= 200 && this.status < 300) scheduleReconcile();
          });
        }
      } catch (e) { /* never block the request */ }
      return open.apply(this, arguments);
    };
  }

  // ---------------------------------------------------------------- boot

  window.GoeyeBundle = {
    keysFor: keysFor,
    removeBundleForKey: removeBundleForKey,
    setBundleQuantity: setBundleQuantity,
    reconcile: reconcile,
    // exposed for cart UIs that classify lines themselves
    isLensLine: isLensLine,
    isFeeLine: isFeeLine,
    bundleIdOf: gidOf
  };

  hookFetch();
  hookXhr();

  // Heal on arrival: this catches a bundle broken by a flow that never ran this file — the plain
  // /cart page form, another app, a sold-out line dropped at checkout, a second browser tab.
  function bootReconcile() {
    setTimeout(function () { reconcile(); }, 600);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootReconcile);
  } else {
    bootReconcile();
  }
})();
